<template>
  <ApexChart v-if="chartData && chartData.series" element="PortFuelCharacteristics" :chartOption="chartData"
    :series="chartData.series" />
</template>

<script>
import moment from 'moment';

export default {
  name: 'PortFuelCharacteristics',
  props: ['series'],
  computed: {
    chartData() {
      if (!this.series) return null;
      const isMobile = window.innerWidth <= 991;

      // 1. Ekstraksi data
      const MappedPortInFlow = this.series.map((data) => {
        const raw = data && data.fmData && Number.isFinite(Number(data.fmData.PORT_InFlow))
          ? Number(data.fmData.PORT_InFlow) : null;
        return Number.isFinite(raw) ? Number(raw.toFixed(2)) : null;
      });
      const MappedPortRpm = this.series.map((data) => {
        const raw = data && data.rpmData && data.rpmData.PORT && Number.isFinite(Number(data.rpmData.PORT.rpm))
          ? Number(data.rpmData.PORT.rpm) : null;
        return Number.isFinite(raw) ? Number(raw.toFixed(2)) : null;
      });
      const mappedPortInDens = this.series.map((data) => {
        const raw = data && data.fmData && Number.isFinite(Number(data.fmData.PORT_InDens))
          ? Number(data.fmData.PORT_InDens) : null;
        return Number.isFinite(raw) ? Number(raw.toFixed(2)) : null;
      });
      const mappedPortInTemp = this.series.map((data) => {
        const raw = data && data.fmData && data.fmData.PORT_IN && Number.isFinite(Number(data.fmData.PORT_IN.temperature))
          ? Number(data.fmData.PORT_IN.temperature) : null;
        return Number.isFinite(raw) ? Number(raw.toFixed(2)) : null;
      });

      // 2. Terapkan interpolasi
      const InterpolatedPortInFlow = this.interpolateNulls(MappedPortInFlow);
      const InterpolatedPortRpm = this.interpolateNulls(MappedPortRpm);
      const InterpolatedPortInDens = this.interpolateNulls(mappedPortInDens);
      const InterpolatedPortInTemp = this.interpolateNulls(mappedPortInTemp);

      const safeMax = (arr, fallback = 10) => {
        const vals = arr.map((v) => (typeof v === 'string' ? Number(v) : v)).filter((v) => Number.isFinite(v));
        return vals.length ? Math.max(...vals) : fallback;
      };

      let MaxInFlow = safeMax(InterpolatedPortInFlow);
      MaxInFlow += (MaxInFlow * 0.10);

      let MaxRpm = safeMax(InterpolatedPortRpm, 1000);
      MaxRpm = (Math.ceil(MaxRpm / 100) * 100) + 100;

      let MaxPortInDens = safeMax(InterpolatedPortInDens);
      MaxPortInDens += (MaxPortInDens * 0.10);

      let MaxPortInTemp = safeMax(InterpolatedPortInTemp);
      MaxPortInTemp += (MaxPortInTemp * 0.10);

      const seriesNames = {
        portInFlow: 'Port In Flow (L/h)',
        portRpm: 'Port RPM',
        portInDens: 'Port In Density (g/cm³)',
        portInTemp: 'Port In Temp (°C)',
      };

      const seriesData = [
        // Port In Flow
        { name: seriesNames.portInFlow, data: MappedPortInFlow, color: '#0084ff' },
        { name: `${seriesNames.portInFlow} (Interpolated)`, data: InterpolatedPortInFlow, color: 'rgba(0, 132, 255, 0.4)' },
        // Port RPM
        { name: seriesNames.portRpm, data: MappedPortRpm, color: '#884DFF' },
        { name: `${seriesNames.portRpm} (Interpolated)`, data: InterpolatedPortRpm, color: 'rgba(136, 77, 255, 0.4)' },
        // Port In Density
        { name: seriesNames.portInDens, data: InterpolatedPortInDens, color: '#10B981' },
        { name: `${seriesNames.portInDens} (Interpolated)`, data: InterpolatedPortInDens, color: 'rgba(16, 185, 129, 0.4)' },
        // Port In Temp
        { name: seriesNames.portInTemp, data: InterpolatedPortInTemp, color: '#F97316' },
        { name: `${seriesNames.portInTemp} (Interpolated)`, data: InterpolatedPortInTemp, color: 'rgba(249, 115, 22, 0.4)' },
      ];

      const legendFillColors = seriesData.map((s) => (s.name.includes('(Interpolated)') ? 'transparent' : s.color));

      return {
        series: seriesData,
        chart: {
          height: 600,
          width: '100%',
          type: 'line',
          stacked: false,
          animations: {
            enabled: false,
          },
          events: {
            legendClick(chartContext, seriesIndex) {
              const seriesName = chartContext.w.globals.seriesNames[seriesIndex];
              const interpolatedName = `${seriesName} (Interpolated)`;
              chartContext.toggleSeries(interpolatedName);
            },
          },
          zoom: {
            enabled: true,
            allowMouseWheelZoom: false,
          },
        },
        markers: {
          size: 4,
        },
        stroke: {
          width: [3, 2, 3, 2, 3, 2, 3, 2],
          dashArray: [0, 8, 0, 8, 0, 8, 0, 8],
        },
        legend: {
          position: 'bottom',
          formatter(seriesName) {
            if (seriesName.includes('(Interpolated)')) {
              return '';
            }
            return seriesName;
          },
          markers: {
            fillColors: legendFillColors,
          },
        },
        tooltip: {
          shared: true,
          custom({
            // eslint-disable-next-line no-unused-vars
            series, seriesIndex, dataPointIndex, w,
          }) {
            const header = w.globals.categoryLabels[dataPointIndex];
            let tooltipContent = '';

            for (let i = 0; i < series.length; i++) {
              if (!w.globals.seriesNames[i].includes('(Interpolated)')) {
                const seriesValue = series[i][dataPointIndex];
                if (seriesValue !== null && typeof seriesValue !== 'undefined') {
                  const seriesColor = w.globals.colors[i];
                  const seriesName = w.globals.seriesNames[i];
                  tooltipContent += `
                    <div class="apexcharts-tooltip-series-group" style="order: ${i}; display: flex;">
                      <span class="apexcharts-tooltip-marker" style="background-color: ${seriesColor};"></span>
                      <div class="apexcharts-tooltip-text" style="font-family: Helvetica, Arial, sans-serif; font-size: 12px;">
                        <div class="apexcharts-tooltip-y-group">
                          <span class="apexcharts-tooltip-text-y-label">${seriesName}: </span>
                          <span class="apexcharts-tooltip-text-y-value">${seriesValue}</span>
                        </div>
                      </div>
                    </div>`;
                }
              }
            }

            if (tooltipContent === '') {
              return '';
            }

            return `
              <div class="apexcharts-tooltip-title" style="font-family: Helvetica, Arial, sans-serif; font-size: 12px;">
                ${header}
              </div>
              <div class="apexcharts-tooltip-series-group-wrapper" style="display: flex; flex-direction: column;">
                ${tooltipContent}
              </div>`;
          },
        },
        xaxis: {
          type: 'datetime',
          labels: {
            formatter(val) {
              return moment(val).format('DD-MM-YYYY HH:mm');
            },
          },
          categories: this.series.map((data) => moment(data.timestamp * 1000).format('DD MMMM YYYY HH:mm')),
          tickAmount: isMobile ? 3 : undefined,
        },
        yaxis: [
          // Port Flow Axis
          {
            max: MaxInFlow,
            title: { text: 'Port Flow (L/h)' },
            show: !isMobile,
          },
          { max: MaxInFlow, show: false },
          // Port RPM Axis
          {
            opposite: true,
            max: MaxRpm,
            title: { text: 'Port RPM' },
            show: !isMobile,
          },
          { opposite: true, max: MaxRpm, show: false },
          // Port Density Axis
          {
            opposite: true,
            max: MaxPortInDens,
            title: { text: 'Port Density (g/cm³)' },
            show: !isMobile,
          },
          { opposite: true, max: MaxPortInDens, show: false },
          // Port Temp Axis
          {
            opposite: true,
            max: MaxPortInTemp,
            title: { text: 'Port Temperature (°C)' },
            show: !isMobile,
          },
          { opposite: true, max: MaxPortInTemp, show: false },
        ],
      };
    },
  },
  methods: {
    interpolateNulls(dataArray) {
      const result = [...dataArray];
      for (let i = 0; i < result.length; i++) {
        if (result[i] === null) {
          const startIndex = i - 1;
          const startValue = startIndex >= 0 ? result[startIndex] : null;

          if (startValue !== null) {
            let endIndex = -1;
            for (let j = i + 1; j < result.length; j++) {
              if (result[j] !== null) {
                endIndex = j;
                break;
              }
            }

            if (endIndex !== -1) {
              const endValue = result[endIndex];
              const gapCount = endIndex - startIndex;
              const totalDifference = endValue - startValue;
              const step = totalDifference / gapCount;

              for (let k = startIndex + 1; k < endIndex; k++) {
                const interpolatedValue = startValue + step * (k - startIndex);
                result[k] = Number(interpolatedValue.toFixed(2));
              }
              i = endIndex - 1;
            }
          }
        }
      }
      return result;
    },
  },
};
</script>

<style scoped></style>
